import React, { useEffect, useState, useMemo } from 'react';
import { MessageBox, Tree, message, Input, Tag, Tooltip } from 'kenshin';
import { getServcatgList } from '@/_serveice/project';

const { Search } = Input;

const ServiceCategoryModal = ({
  visible,
  onCancel,
  onOk,
  checkedKeys: defaultCheckedKeys,
}) => {
  const [treeData, setTreeData] = useState([]);
  const [checkedKeys, setCheckedKeys] = useState([]);
  const [tagMap, setTagMap] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  // 获取所有父节点key，用于默认展开所有
  const getAllKeys = (data) => {
    let keys = [];
    data.forEach((item) => {
      keys.push(item.key);
      if (item.children) {
        keys = [...keys, ...getAllKeys(item.children)];
      }
    });
    return keys;
  };

  useEffect(() => {
    if (visible) {
      setCheckedKeys([]); // 先清空，等待数据加载完成后计算
      setSearchValue(''); // 重置搜索

      getServcatgList({ type: 1 }).then((res) => {
        if (res?.success) {
          const { tree, map } = processTreeData([res.data]);
          setTreeData(tree);
          setTagMap(map);
          setExpandedKeys(getAllKeys(tree)); // 默认展开所有
          // 数据加载完成后，计算回显
          if (Array.isArray(defaultCheckedKeys)) {
            const keysToCheck = [];
            Object.keys(map).forEach((key) => {
              if (defaultCheckedKeys.includes(map[key].originalId)) {
                keysToCheck.push(key);
              }
            });
            setCheckedKeys(keysToCheck);
          }
        }
      });
    }
  }, [visible]);

  const processTreeData = (data, map = {}) => {
    if (!Array.isArray(data)) return { tree: [], map };
    const tree = data.map((item) => {
      let children = [];
      const currentNodeKey = item.servCatgId || item.id;

      // 处理标签列表
      if (item.taskTagList && Array.isArray(item.taskTagList)) {
        const tagChildren = item.taskTagList.map((tag, index) => {
          // 生成唯一key: tag-父节点ID-标签ID-索引
          // 这样即使不同父节点下有相同ID的tag，key也是唯一的
          const key = `tag-${currentNodeKey}-${tag.id}-${index}`;
          map[key] = { ...tag, originalId: tag.id };

          // 为标签添加Tooltip，仅当extendData不为空时使用
          let tagTitle = tag.value;
          if (tag.extendData) {
            tagTitle = <Tooltip title={tag.extendData}>{tag.value}</Tooltip>;
          }

          return {
            title: tagTitle,
            key: key,
            isLeaf: true,
            isTag: true,
            extendData: tag.extendData, // 保存extendData用于搜索过滤时使用
            // ...tag // 避免把 id 覆盖了
          };
        });
        children = [...children, ...tagChildren];
      }
      // 递归处理子节点
      if (item.child) {
        const result = processTreeData(item.child, map);
        children = [...children, ...result.tree];
      }

      return {
        title: item.servCatgName || item.extendData,
        key: currentNodeKey,
        disabled: false, // 允许展开
        disableCheckbox: !(
          item.taskTagList &&
          Array.isArray(item.taskTagList) &&
          item.taskTagList.length > 0
        ), // taskTagList不为空的父节点可以勾选
        selectable: false, // 不可点选
        children: children.length > 0 ? children : undefined,
      };
    });
    return { tree, map };
  };
  // console.log('treeData', treeData);

  // 获取指定节点的所有子节点key
  const getAllChildKeys = (tree, parentKey) => {
    let childKeys = [];

    const findNode = (nodes) => {
      for (const node of nodes) {
        if (node.key === parentKey) {
          // 找到父节点，收集其所有子节点
          const collectChildren = (childrenNodes) => {
            if (childrenNodes) {
              for (const child of childrenNodes) {
                childKeys.push(child.key);
                if (child.children) {
                  collectChildren(child.children);
                }
              }
            }
          };
          collectChildren(node.children);
          return true;
        }
        if (node.children && findNode(node.children)) {
          return true;
        }
      }
      return false;
    };

    findNode(tree);
    return childKeys;
  };

  const handleCheck = (keys) => {
    // 1. 获取当前展示树的所有key
    const visibleKeys = getAllKeys(displayTreeData);

    // 2. 找出之前选中但现在被隐藏（不在展示树中）的key
    const hiddenKeys = checkedKeys.filter((key) => !visibleKeys.includes(key));

    // 3. 处理父节点勾选逻辑：如果父节点被勾选，自动勾选所有子节点
    const allKeys = [...hiddenKeys, ...keys];
    let finalKeys = [...allKeys];

    // 遍历所有节点，检查是否有父节点被勾选
    const processTreeForCheck = (nodes) => {
      for (const node of nodes) {
        if (allKeys.includes(node.key) && node.children) {
          // 父节点被勾选，收集所有子节点key
          const childKeys = getAllChildKeys(displayTreeData, node.key);
          // 将子节点添加到finalKeys中
          childKeys.forEach((key) => {
            if (!finalKeys.includes(key)) {
              finalKeys.push(key);
            }
          });
        }
        if (node.children) {
          processTreeForCheck(node.children);
        }
      }
    };

    processTreeForCheck(displayTreeData);

    setCheckedKeys(finalKeys);
  };

  const onExpand = (expandedKeys) => {
    setExpandedKeys(expandedKeys);
    setAutoExpandParent(false);
  };

  const onChange = (e) => {
    const { value } = e.target;
    setSearchValue(value);
    setAutoExpandParent(true);
  };

  // 过滤树数据
  const filterTreeData = (data, value) => {
    if (!value) return data;
    return data
      .map((item) => {
        // 提取原始文本（如果是React元素则获取其children）
        let originalTitle = item.title;
        if (React.isValidElement(originalTitle)) {
          // 如果title是React元素（比如Tooltip），获取其children的文本
          originalTitle = originalTitle.props.children;
        }
        const strTitle = String(originalTitle || '');
        const index = strTitle.toLowerCase().indexOf(value.toLowerCase());
        const isMatch = index > -1;

        if (isMatch) {
          // 如果匹配，高亮标题
          const beforeStr = strTitle.substr(0, index);
          const afterStr = strTitle.substr(index + value.length);
          const matchStr = strTitle.substr(index, value.length);
          const highlightedTitle = (
            <span>
              {beforeStr}
              <span style={{ color: '#ff5000' }}>{matchStr}</span>
              {afterStr}
            </span>
          );

          // 判断是否需要添加Tooltip（仅标签节点有extendData）
          let title = highlightedTitle;
          if (item.isTag && item.extendData) {
            title = (
              <Tooltip title={item.extendData}>{highlightedTitle}</Tooltip>
            );
          }

          return { ...item, title };
        }

        // 如果不匹配，检查子节点
        if (item.children) {
          const children = filterTreeData(item.children, value).filter(Boolean);
          if (children.length > 0) {
            return { ...item, children };
          }
        }

        return null;
      })
      .filter(Boolean);
  };

  const displayTreeData = useMemo(() => {
    return filterTreeData(treeData, searchValue);
  }, [treeData, searchValue]);

  // 当搜索结果变化时，展开所有相关节点
  useEffect(() => {
    if (searchValue && autoExpandParent) {
      setExpandedKeys(getAllKeys(displayTreeData));
    }
  }, [displayTreeData, searchValue, autoExpandParent]);

  const handleOk = () => {
    // 收集所有选中的标签
    const selectedTags = checkedKeys
      .filter((key) => key.startsWith('tag-') && tagMap[key])
      .map((key) => tagMap[key]);

    // 根据 originalId 去重
    const uniqueTags = [];
    const seenIds = new Set();

    selectedTags.forEach((tag) => {
      if (!seenIds.has(tag.originalId)) {
        seenIds.add(tag.originalId);
        uniqueTags.push({
          ...tag,
          id: tag.originalId, // 还原ID
        });
      }
    });

    if (onOk) {
      onOk(uniqueTags);
    }
    onCancel();
  };

  const handleRemoveTag = (originalId) => {
    // 找到所有对应这个 originalId 的 keys 并移除
    const newCheckedKeys = checkedKeys.filter((key) => {
      const tag = tagMap[key];
      return !tag || tag.originalId !== originalId;
    });
    setCheckedKeys(newCheckedKeys);
  };

  const renderSelectedTags = () => {
    // 收集所有选中的标签
    const selectedTags = checkedKeys
      .filter((key) => key.startsWith('tag-') && tagMap[key])
      .map((key) => tagMap[key]);

    // 根据 originalId 去重
    const uniqueTags = [];
    const seenIds = new Set();

    selectedTags.forEach((tag) => {
      if (!seenIds.has(tag.originalId)) {
        seenIds.add(tag.originalId);
        uniqueTags.push({
          ...tag,
          id: tag.originalId,
        });
      }
    });

    if (uniqueTags.length === 0) return null;

    return (
      <div style={{ marginBottom: 8, maxHeight: 100, overflowY: 'auto' }}>
        {uniqueTags.map((tag) => (
          <Tag
            key={tag.id}
            closable
            onClose={() => handleRemoveTag(tag.id)}
            type="primary"
            style={{ marginBottom: 4, marginRight: 4 }}
          >
            {tag.value}
          </Tag>
        ))}
      </div>
    );
  };

  return (
    <MessageBox
      title="选择服务类目标签"
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      width={600}
      zIndex={1002}
      bodyStyle={{ height: 400, overflow: 'auto' }}
    >
      {renderSelectedTags()}
      <Search
        style={{ marginBottom: 8 }}
        placeholder="搜索服务类目"
        onChange={onChange}
        value={searchValue}
      />
      {treeData.length > 0 && (
        <Tree
          treeData={displayTreeData}
          checkable
          checkedKeys={checkedKeys}
          onCheck={handleCheck}
          expandedKeys={expandedKeys}
          autoExpandParent={autoExpandParent}
          onExpand={onExpand}
        />
      )}
    </MessageBox>
  );
};

export default ServiceCategoryModal;
